import { useEffect, useState } from 'react';

import { Button } from '@components/button';
import { createForm, useCreateFormStore } from '@components/form';
import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconEdit } from '@components/icons/edit';
import { IconEndpoint } from '@components/icons/endpoint';
import { IconTrash } from '@components/icons/trash';
import { Sidebar, hideSidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { StatusOptionLabel } from '@components/status-option-label';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { useScopedTranslate } from '@locale/translate';

import { endpointColorMap } from '../common';
import {
  useCreateIntegrationEndpoint,
  useIntegration,
  useUpdateIntegrationEndpoint,
} from '../integrations-hooks';
import {
  endpointMethods,
  IntegrationEndpointCreateType,
  requestTypes,
  RequestTypes,
  type EndpointMethod,
  type IntegrationEndpoint,
} from '../integrations-types';

const Form = createForm<IntegrationEndpointCreateType>();

export const useIntegrationEndpointCreateSidebar = () =>
  useSidebar(IntegrationEndpointCreateSidebar, { variant: 'REGULAR' });

const IntegrationEndpointCreateSidebar = ({
  integrationId,
  endpoint,
}: {
  integrationId: string;
  endpoint?: IntegrationEndpoint<string>;
}) => {
  const { t } = useScopedTranslate(locale);
  const createMutation = useCreateIntegrationEndpoint();
  const updateMutation = useUpdateIntegrationEndpoint();
  const integrationQuery = useIntegration(integrationId);
  const isEdit = Boolean(endpoint);

  const baseUrl = integrationQuery.data?.baseUrl ?? '';
  const isUsingBaseUrl =
    Boolean(baseUrl) && (!isEdit || Boolean(endpoint?.url?.startsWith(baseUrl)));

  const removeBaseUrl = (url: string) => {
    return url.replace(baseUrl, '');
  };

  const handleSubmit = async (data: IntegrationEndpointCreateType) => {
    const url = isUsingBaseUrl ? `${baseUrl}${formUrl}` : formUrl;
    const payload = { ...data, url };
    if (endpoint) {
      await updateMutation.mutateAsync({ data: payload, shortId: endpoint.shortId });
    } else {
      await createMutation.mutateAsync({ ...payload, integration: integrationId });
    }
    hideSidebar();
  };
  const formStore = useCreateFormStore();
  const requestType = formStore.useFieldValue('requestType');
  const requestMethod = formStore.useFieldValue('method') as EndpointMethod;
  const schemaUrl = formStore.useFieldValue('schemaUrl') as string;
  const formUrl = formStore.useFieldValue('url') as string;
  const isFormDirty = formStore.useIsDirty();
  const [isMultipartDirty, setIsMultipartDirty] = useState(false);
  const isDirty = isFormDirty || isMultipartDirty;
  const isLoading = createMutation.isLoading || updateMutation.isLoading;
  const shouldContainExampleRequest = ['POST', 'PUT', 'PATCH'].includes(requestMethod);

  const availableMethods =
    requestType === 'file'
      ? endpointMethods.filter((method) => method === 'POST' || method === 'PUT')
      : endpointMethods;

  const requestTypeLabelMap: Record<RequestTypes, string> = {
    json: 'JSON',
    multipart: 'Multipart',
    graphql: 'GraphQL',
    file: 'File',
  };

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        icon={isEdit ? IconEdit : IconEndpoint}
        iconColor="elementBlue100"
        title={isEdit ? t('editEndpoint') : t('addEndpoint')}
      />
      <Sidebar.Content>
        <Form onSubmit={handleSubmit} store={formStore}>
          <Form.TextField
            id="name"
            label={t('name')}
            validators={['required']}
            defaultValue={endpoint?.name ?? ''}
            placeholder="e.g. Create journey"
          />
          {isUsingBaseUrl ? (
            <Stack space={1}>
              <Form.TextField
                id="url"
                label={t('path')}
                validators={['required']}
                defaultValue={endpoint?.url ? removeBaseUrl(endpoint.url) : ''}
                placeholder="e.g. /v1/journeys/"
              />
              <Text font="regular12" text={`${baseUrl}${formUrl}`} color="textHelper" />
            </Stack>
          ) : (
            <Form.TextField
              id="url"
              label={t('url')}
              validators={['valid-http-url', 'required']}
              defaultValue={endpoint?.url ?? ''}
              placeholder="e.g. https://journeys-api.50skills.app/v1/journeys/"
            />
          )}
          <Form.Select
            id="requestType"
            label={t('requestType')}
            isClearable={false}
            options={requestTypes.map((requestType) => ({
              label: requestTypeLabelMap[requestType],
              value: requestType,
            }))}
            defaultValue={endpoint?.requestType ?? requestTypes[0]}
          />
          {requestType !== 'graphql' && (
            <Form.Select
              id="method"
              label={t('method')}
              options={availableMethods.map((endpointMethod) => ({
                label: (
                  <StatusOptionLabel
                    text={endpointMethod}
                    color={endpointColorMap[endpointMethod]}
                  />
                ),
                value: endpointMethod,
              }))}
              isClearable={false}
              defaultValue={endpoint?.method ?? 'POST'}
            />
          )}
          {requestType === 'multipart' && shouldContainExampleRequest && (
            <MultipartExample
              formStore={formStore}
              endpoint={endpoint}
              setIsMultipartDirty={setIsMultipartDirty}
            />
          )}
          {requestType === 'file' && (
            <Stack space={2}>
              <Text font="regular14" text={t('fileRequestInfo')} color="textHelper" />
            </Stack>
          )}
          {requestType === 'json' && shouldContainExampleRequest && (
            <Form.JsonEditor
              id="exampleData"
              label={t('exampleRequest')}
              initialValue={endpoint?.exampleData}
            />
          )}
          {requestType === 'graphql' && (
            <>
              <Form.TextField
                id="schemaUrl"
                label="Schema URL"
                defaultValue={endpoint?.schemaUrl ?? ''}
                placeholder="e.g. https://api.monday.com/v2/get_schema?format=sdl"
              />
              <Form.GraphQLEditor
                id="graphqlRequest"
                label={t('graphqlRequest')}
                initialValue={endpoint?.graphqlRequest}
                schemaURL={schemaUrl}
              />
              <Form.JsonEditor
                id="exampleData"
                label={t('graphqlVariables')}
                initialValue={endpoint?.exampleData}
              />
            </>
          )}
          <Sidebar.Footer>
            <Form.Actions>
              <Button text={t('core.cancel')} variant="secondary" onClick={hideSidebar} />
              <Form.Submit
                variant="primary-blue"
                text={isEdit ? t('core.save') : t('addEndpoint')}
                isDisabled={!isDirty || isLoading}
              />
            </Form.Actions>
          </Sidebar.Footer>
        </Form>
      </Sidebar.Content>
    </Sidebar>
  );
};

const MultipartExample = ({
  formStore,
  setIsMultipartDirty,
  endpoint,
}: {
  formStore: ReturnType<typeof useCreateFormStore>;
  setIsMultipartDirty: React.Dispatch<React.SetStateAction<boolean>>;
  endpoint?: IntegrationEndpoint<string>;
}) => {
  const { t } = useScopedTranslate(locale);
  const initialFields = endpoint?.variables.map((variable) => ({ key: variable.name })) ?? [
    { key: '' },
  ];
  const [fields, setFields] = useState<{ key: string }[]>(initialFields);

  useEffect(() => {
    const exampleData = fields.reduce<{ [key: string]: string }>((acc, field) => {
      acc[field.key] =
        (endpoint?.variables.find((variable) => variable.name === field.key)?.example as string) ??
        '';
      return acc;
    }, {});
    formStore.actions.setValue('exampleData', JSON.stringify(exampleData, null, 2));
  }, [fields, formStore, endpoint]);

  const updateFields = (updatedFields: typeof fields) => {
    setIsMultipartDirty(true);
    setFields(updatedFields);
  };

  const handleRemoveField = (indexToRemove: number) => {
    const updatedFields = fields.filter((_, index) => index !== indexToRemove);
    updateFields(updatedFields);
  };

  return (
    <Stack space={4}>
      <Text font="bold14" text={t('fields')} />
      <Stack space={2}>
        {fields.map((field, index) => (
          <Stack orientation="horizontal" key={index} space={2} justify="space-between">
            <div style={{ width: '100%' }}>
              <TextField
                id="key"
                label={t('field')}
                hideLabel
                placeholder={t('fieldPlaceholder')}
                value={field.key}
                onChange={(value) => {
                  const updatedFields = [...fields];
                  updatedFields[index].key = value;
                  updateFields(updatedFields);
                }}
              />
            </div>
            <IconButton icon={<IconTrash />} onClick={() => handleRemoveField(index)} />
          </Stack>
        ))}
      </Stack>
      <Stack orientation="horizontal" justify="flex-end">
        <Button
          text={t('addField')}
          variant="secondary"
          iconLeft={<IconAddPlus />}
          onClick={() => {
            updateFields([...fields, { key: '' }]);
          }}
        />
      </Stack>
    </Stack>
  );
};

const locale = {
  en: {
    addEndpoint: 'Add Endpoint',
    editEndpoint: 'Edit Endpoint',
    name: 'Name',
    url: 'URL',
    submit: 'Submit',
    exampleRequest: 'Example request',
    graphqlRequest: 'GraphQL request',
    graphqlVariables: 'Example variables',
    method: 'Method',
    requestType: 'Request type',
    fieldType: 'Field type',
    field: 'Field',
    fieldPlaceholder: 'Write field name here',
    addField: 'Add field',
    fields: 'Fields',
    fileRequestInfo:
      'File requests send a single file as the entire request body. A file variable will be automatically created.',
    path: 'Path',
  },
};
